import { getDbSupabase } from './supabase';
import { formatDeliveryDateDisplay } from './demDeliveryDate';
import { formatSupabaseError } from './formatSupabaseError';
import {
  collectCustomerCodesFromRows,
  fetchCustomerNamesByCodes,
  findCustomerCodesMatchingSearch,
} from './demCustomerNames';
import {
  fetchDemUserDisplayNameMap,
  findDemActorIdsMatchingSearch,
  resolveDemDisplayUser,
} from './demUserNames';
import { formatCheckedAtDisplay, orderNeedsCheck } from './demOrderChecked';
import { todayDisplaySgt } from './demPoOrders';
import {
  formatOrderCreatorDisplay,
  formatOrderExporterDisplay,
  parseOrderBatches,
} from './demOrderBatches';
import type {
  DemCallInOrder,
  DemOrderChannelFilter,
  DemOrderSource,
  DemPoOrder,
  DemPoOrderFilter,
  DemPoOrderItem,
  DemUnifiedOrder,
  DemWebOrder,
} from '../types/dem';

const ORDER_SELECT =
  'order_id, created_at, delivery_date, customer_code, ref_no, status, source, created_by, exported_by, checked_at, checked_by, remarks, contact_person, contact_no, email, total_amount, waive_delivery, processing_seconds, items:order_items(item_code, description, qty, uom, batch_id), batches:order_batches(id, order_id, batch_no, reason, created_by, created_at, exported_by, exported_at)';

const INITIAL_PAGE = 50;
const MORE_PAGE = 100;

const ALL_SOURCES: DemOrderSource[] = ['Desktop', 'Web', 'WhatsApp', 'CallIn'];

export const CHANNEL_TO_SOURCES: Record<DemOrderChannelFilter, DemOrderSource[]> = {
  All: ALL_SOURCES,
  PO: ['Desktop'],
  B2B: ['Web'],
  WhatsApp: ['WhatsApp'],
  Manual: ['CallIn'],
};

export function channelLabel(channel: DemOrderChannelFilter): string {
  switch (channel) {
    case 'All':
      return 'All';
    case 'PO':
      return 'PO';
    case 'B2B':
      return 'B2B';
    case 'WhatsApp':
      return 'WhatsApp';
    case 'Manual':
      return 'Manual';
    default:
      return channel;
  }
}

export function sourceToChannel(source: DemOrderSource): Exclude<DemOrderChannelFilter, 'All'> {
  switch (source) {
    case 'Desktop':
      return 'PO';
    case 'Web':
      return 'B2B';
    case 'WhatsApp':
      return 'WhatsApp';
    case 'CallIn':
      return 'Manual';
    default:
      return 'PO';
  }
}

export function sourceChipLabel(source: DemOrderSource): string {
  return sourceToChannel(source);
}

/** Short list chip: PO / B2B / WA / MI */
export function sourceChipAbbr(source: DemOrderSource): string {
  switch (source) {
    case 'Desktop':
      return 'PO';
    case 'Web':
      return 'B2B';
    case 'WhatsApp':
      return 'WA';
    case 'CallIn':
      return 'MI';
    default:
      return 'PO';
  }
}

/** CSS modifier for source chip (po / b2b / wa / mi). */
export function sourceChipClass(source: DemOrderSource): string {
  return sourceChipAbbr(source).toLowerCase();
}

function parseCreatedAtDisplay(raw: string | null | undefined): string {
  if (!raw) return '—';
  try {
    const normalized = raw.includes('T') ? raw.replace('Z', '+00:00') : `${raw.replace(' ', 'T')}+00:00`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return raw;
  }
}

function parseItems(raw: unknown): DemPoOrderItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      item_code: String(r.item_code ?? '').trim(),
      description: String(r.description ?? '').trim(),
      qty: Number(r.qty ?? 0),
      uom: String(r.uom ?? '').trim(),
      batch_id: r.batch_id == null ? null : String(r.batch_id).trim() || null,
    };
  });
}

function hasDeliveryFee(items: DemPoOrderItem[]): boolean {
  return items.some((item) => {
    const code = item.item_code;
    const desc = item.description;
    return (
      code === 'NEW PRODUCT' ||
      code === '401090' ||
      code === '601158' ||
      desc.includes('401090') ||
      desc.includes('601158') ||
      desc.includes('Delivery Charge') ||
      desc.includes('Delivery Fee')
    );
  });
}

function parseSource(raw: unknown): DemOrderSource {
  const value = String(raw ?? '').trim();
  if (value === 'Web' || value === 'WhatsApp' || value === 'CallIn' || value === 'Desktop') {
    return value;
  }
  return 'Desktop';
}

function mapOrderRow(
  row: Record<string, unknown>,
  customerNames: Map<string, string>,
  userNames: Map<string, string>,
): DemUnifiedOrder {
  const customerCode = String(row.customer_code ?? '').trim();
  const items = parseItems(row.items);
  const source = parseSource(row.source);
  const createdByRaw = String(row.created_by ?? '').trim();
  const createdByFallback =
    source === 'Web' && !createdByRaw ? 'Customer Portal' : createdByRaw;
  const batchesRaw = parseOrderBatches(row.batches);
  const batches = batchesRaw.map((batch) => ({
    ...batch,
    created_by: resolveDemDisplayUser(
      batch.created_by.trim() || (batch.batch_no === 0 ? createdByFallback : ''),
      userNames,
    ),
    exported_by: batch.exported_by
      ? resolveDemDisplayUser(batch.exported_by, userNames)
      : null,
  }));

  return {
    source,
    order_id: String(row.order_id ?? ''),
    customer_code: customerCode,
    customer_name: (customerCode ? customerNames.get(customerCode) : null) ?? 'Unknown',
    created_at: String(row.created_at ?? ''),
    created_at_display: parseCreatedAtDisplay(String(row.created_at ?? '')),
    delivery_date: String(row.delivery_date ?? ''),
    delivery_date_display: formatDeliveryDateDisplay(String(row.delivery_date ?? '')),
    ref_no: String(row.ref_no ?? '').trim() || '—',
    status: String(row.status ?? ''),
    remarks: String(row.remarks ?? '').trim() || '—',
    contact_person: String(row.contact_person ?? '').trim() || '—',
    contact_no: String(row.contact_no ?? '').trim() || '—',
    email: String(row.email ?? '').trim() || '—',
    total_amount: Number(row.total_amount ?? 0),
    waive_delivery: Boolean(row.waive_delivery),
    created_by: formatOrderCreatorDisplay(batchesRaw, userNames, createdByFallback),
    created_by_id: createdByFallback,
    exported_by: formatOrderExporterDisplay(batchesRaw, userNames, ''),
    checked_at: row.checked_at ? String(row.checked_at) : null,
    checked_at_display: formatCheckedAtDisplay(row.checked_at ? String(row.checked_at) : null),
    checked_by: String(row.checked_by ?? '').trim()
      ? resolveDemDisplayUser(String(row.checked_by ?? ''), userNames)
      : '',
    processing_seconds: (() => {
      const raw = row.processing_seconds;
      if (raw == null || raw === '') return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    items,
    batches,
    has_delivery_fee: hasDeliveryFee(items),
  };
}

async function mapOrderRows(rows: Record<string, unknown>[]): Promise<DemUnifiedOrder[]> {
  const [customerNames, userNames] = await Promise.all([
    fetchCustomerNamesByCodes(collectCustomerCodesFromRows(rows)),
    fetchDemUserDisplayNameMap(),
  ]);
  return rows.map((row) => mapOrderRow(row, customerNames, userNames));
}

function escapeIlikePattern(value: string): string {
  return value.replace(/[%_]/g, '\\$&');
}

function mergeOrderRawRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  const map = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    const id = String(row.order_id ?? '').trim();
    if (!id) continue;
    map.set(id, row);
  }
  return [...map.values()].sort((a, b) => {
    const byTime = String(b.created_at ?? '').localeCompare(String(a.created_at ?? ''));
    if (byTime !== 0) return byTime;
    return String(b.order_id ?? '').localeCompare(String(a.order_id ?? ''));
  });
}

/** Cap per search branch so pagination can slice a merged, sorted window. */
const SEARCH_BRANCH_CAP = 500;

export async function fetchUnifiedOrdersPage(
  offset: number,
  limit: number,
  channel: DemOrderChannelFilter,
  searchKw?: string,
): Promise<DemUnifiedOrder[]> {
  const db = getDbSupabase('dem');
  const sources = CHANNEL_TO_SOURCES[channel];
  const kw = searchKw?.trim();

  if (!kw) {
    const { data, error } = await db
      .from('orders')
      .select(ORDER_SELECT)
      .in('source', sources)
      .neq('status', 'Archived')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (error) throw new Error(formatSupabaseError(error));
    return mapOrderRows((data ?? []) as Record<string, unknown>[]);
  }

  // Orders store customer_code / actor ids only; resolve name hits, then merge branches.
  const escaped = escapeIlikePattern(kw);
  const [matchingCodes, actorIds] = await Promise.all([
    findCustomerCodesMatchingSearch(kw),
    findDemActorIdsMatchingSearch(kw),
  ]);
  const branchEnd = Math.min(SEARCH_BRANCH_CAP, offset + limit) - 1;
  const fieldFilter = [
    `order_id.ilike.%${escaped}%`,
    `ref_no.ilike.%${escaped}%`,
    `customer_code.ilike.%${escaped}%`,
    `remarks.ilike.%${escaped}%`,
    `exported_by.ilike.%${escaped}%`,
    `created_by.ilike.%${escaped}%`,
  ].join(',');

  const ordersBase = () => db.from('orders').select(ORDER_SELECT).in('source', sources);

  const fieldQuery = ordersBase()
    .or(fieldFilter)
    .order('created_at', { ascending: false })
    .range(0, branchEnd);

  const empty = Promise.resolve({ data: [] as unknown[], error: null });

  const customerQuery =
    matchingCodes.length > 0
      ? ordersBase()
          .in('customer_code', matchingCodes)
          .order('created_at', { ascending: false })
          .range(0, branchEnd)
      : null;

  const exportedByQuery =
    actorIds.length > 0
      ? ordersBase()
          .in('exported_by', actorIds)
          .order('created_at', { ascending: false })
          .range(0, branchEnd)
      : null;

  const createdByQuery =
    actorIds.length > 0
      ? ordersBase()
          .in('created_by', actorIds)
          .order('created_at', { ascending: false })
          .range(0, branchEnd)
      : null;

  // Batch exporters are the source of truth for list "Exported by" when multi-batch.
  const batchOrderIdsPromise =
    actorIds.length > 0
      ? db
          .from('order_batches')
          .select('order_id')
          .in('exported_by', actorIds)
          .limit(SEARCH_BRANCH_CAP)
      : empty;

  const [fieldRes, customerRes, exportedByRes, createdByRes, batchRes] = await Promise.all([
    fieldQuery,
    customerQuery ?? empty,
    exportedByQuery ?? empty,
    createdByQuery ?? empty,
    batchOrderIdsPromise,
  ]);

  if (fieldRes.error) throw new Error(formatSupabaseError(fieldRes.error));
  if (customerRes.error) throw new Error(formatSupabaseError(customerRes.error));
  if (exportedByRes.error) throw new Error(formatSupabaseError(exportedByRes.error));
  if (createdByRes.error) throw new Error(formatSupabaseError(createdByRes.error));
  if (batchRes.error) throw new Error(formatSupabaseError(batchRes.error));

  const batchOrderIds = [
    ...new Set(
      (batchRes.data ?? [])
        .map((row) => String((row as Record<string, unknown>).order_id ?? '').trim())
        .filter(Boolean),
    ),
  ];

  let batchOrders: Record<string, unknown>[] = [];
  if (batchOrderIds.length > 0) {
    const { data, error } = await ordersBase()
      .in('order_id', batchOrderIds)
      .order('created_at', { ascending: false })
      .range(0, branchEnd);
    if (error) throw new Error(formatSupabaseError(error));
    batchOrders = (data ?? []) as Record<string, unknown>[];
  }

  const merged = mergeOrderRawRows([
    ...((fieldRes.data ?? []) as Record<string, unknown>[]),
    ...((customerRes.data ?? []) as Record<string, unknown>[]),
    ...((exportedByRes.data ?? []) as Record<string, unknown>[]),
    ...((createdByRes.data ?? []) as Record<string, unknown>[]),
    ...batchOrders,
  ]).slice(offset, offset + limit);

  return mapOrderRows(merged);
}

export function mergeUnifiedOrders(
  existing: DemUnifiedOrder[],
  incoming: DemUnifiedOrder[],
): DemUnifiedOrder[] {
  const map = new Map(existing.map((order) => [order.order_id, order]));
  for (const order of incoming) {
    map.set(order.order_id, order);
  }
  return [...map.values()].sort((a, b) => {
    const byTime = b.created_at.localeCompare(a.created_at);
    if (byTime !== 0) return byTime;
    return b.order_id.localeCompare(a.order_id);
  });
}

export function filterUnifiedOrdersClient(
  orders: DemUnifiedOrder[],
  search: string,
  statusFilter: DemPoOrderFilter,
): DemUnifiedOrder[] {
  const keywords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const today = todayDisplaySgt();

  return orders.filter((order) => {
    if (statusFilter === 'Pending' && order.status !== 'Pending') return false;
    if (
      statusFilter === 'TodayProcessed' &&
      (order.status !== 'Processed' || !order.created_at_display.includes(today))
    ) {
      return false;
    }
    if (statusFilter === 'NeedsCheck' && !orderNeedsCheck(order)) {
      return false;
    }

    if (keywords.length === 0) return true;

    const itemsStr = order.items.map((item) => `${item.item_code} ${item.description}`).join(' ');
    const pool = [
      order.order_id,
      order.ref_no,
      order.customer_code,
      order.customer_name,
      order.status,
      order.remarks,
      order.created_by,
      order.exported_by,
      order.checked_by,
      sourceChipAbbr(order.source),
      sourceChipLabel(order.source),
      itemsStr,
    ]
      .join(' ')
      .toLowerCase();

    return keywords.every((kw) => pool.includes(kw));
  });
}

export function unifiedToPoOrder(order: DemUnifiedOrder): DemPoOrder {
  return {
    order_id: order.order_id,
    customer_code: order.customer_code,
    customer_name: order.customer_name,
    created_at: order.created_at,
    created_at_display: order.created_at_display,
    delivery_date: order.delivery_date,
    delivery_date_display: order.delivery_date_display,
    ref_no: order.ref_no,
    status: order.status,
    created_by: order.created_by,
    exported_by: order.exported_by,
    checked_at: order.checked_at,
    checked_at_display: order.checked_at_display,
    checked_by: order.checked_by,
    processing_seconds: order.processing_seconds ?? null,
    items: order.items,
    batches: order.batches,
  };
}

export function unifiedToCallInOrder(order: DemUnifiedOrder): DemCallInOrder {
  return {
    ...unifiedToPoOrder(order),
    remarks: order.remarks === '—' ? '' : order.remarks,
  };
}

export function unifiedToWebOrder(order: DemUnifiedOrder): DemWebOrder {
  return {
    order_id: order.order_id,
    customer_code: order.customer_code,
    customer_name: order.customer_name,
    created_at: order.created_at,
    created_at_display: order.created_at_display,
    delivery_date: order.delivery_date,
    delivery_date_display: order.delivery_date_display,
    ref_no: order.ref_no,
    status: order.status,
    remarks: order.remarks,
    contact_person: order.contact_person,
    contact_no: order.contact_no,
    email: order.email,
    total_amount: order.total_amount,
    waive_delivery: order.waive_delivery,
    created_by: order.created_by,
    exported_by: order.exported_by,
    checked_at: order.checked_at,
    checked_at_display: order.checked_at_display,
    checked_by: order.checked_by,
    processing_seconds: order.processing_seconds ?? null,
    items: order.items,
    batches: order.batches,
    has_delivery_fee: order.has_delivery_fee,
  };
}

export const UNIFIED_ORDERS_INITIAL_PAGE = INITIAL_PAGE;
export const UNIFIED_ORDERS_MORE_PAGE = MORE_PAGE;
